import { Link } from "react-router-dom";
import StatusTag from "@/components/base/StatusTag";

export interface ClueItem {
  slug: string;
  title: string;
  categorySlug: string;
  categoryName: string;
  summary: string;
  region: string;
  date: string;
  status: string;
  tags: string[];
  views: number;
  cover: string;
}

interface ClueCardProps {
  clue: ClueItem;
  variant?: "vertical" | "horizontal";
}

function ClueCard({ clue, variant = "vertical" }: ClueCardProps) {
  if (variant === "horizontal") {
    return (
      <article className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 sm:flex-row">
        <Link
          to={`/clue/${clue.slug}`}
          className="relative block h-44 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-32 sm:w-48"
        >
          <img
            src={clue.cover}
            alt={`${clue.title} - 爆料平台线索配图`}
            title={`${clue.title} - 匿名爆料线索`}
            className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Link
              to={`/category/${clue.categorySlug}`}
              className="whitespace-nowrap rounded-full bg-primary-100 px-2.5 py-0.5 text-[11px] font-label font-medium text-primary-700 transition-colors hover:bg-primary-200"
            >
              {clue.categoryName}
            </Link>
            <StatusTag status={clue.status} />
          </div>
          <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
            <Link to={`/clue/${clue.slug}`} className="transition-colors hover:text-primary-700">
              {clue.title}
            </Link>
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {clue.summary}
          </p>
          <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-map-pin-line" />
              {clue.region}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" />
              {clue.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {clue.views.toLocaleString("zh-CN")}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/clue/${clue.slug}`} className="relative block h-48 w-full overflow-hidden bg-background-200">
        <img
          src={clue.cover}
          alt={`${clue.title} - 爆料平台线索配图`}
          title={`${clue.title} - 匿名爆料线索`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3">
          <StatusTag status={clue.status} />
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <Link
          to={`/category/${clue.categorySlug}`}
          className="mb-2 inline-flex w-fit items-center gap-1 whitespace-nowrap text-[11px] font-label font-medium uppercase tracking-wider text-secondary-700 transition-colors hover:text-primary-600"
        >
          <i className={clue.categorySlug === "internal" ? "ri-file-shield-2-line" : "ri-price-tag-3-line"} />
          {clue.categoryName}
        </Link>
        <h3 className="font-heading text-lg font-bold leading-snug text-foreground-950">
          <Link to={`/clue/${clue.slug}`} className="transition-colors hover:text-primary-700">
            {clue.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">{clue.summary}</p>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {clue.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="whitespace-nowrap rounded-md bg-background-100 px-2 py-0.5 text-[11px] font-label text-foreground-600"
            >
              #{tag}
            </span>
          ))}
        </div>
        <div className="mt-auto flex items-center justify-between border-t border-background-200 pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-map-pin-line" />
            {clue.region}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" />
            {clue.views.toLocaleString("zh-CN")}
          </span>
        </div>
      </div>
    </article>
  );
}

export default ClueCard;